import React, {
  FunctionComponent, useEffect, useState, Fragment,
} from 'react';
import { ConnectedProps, connect } from 'react-redux';
import { SelectionChangedEvent, ColGroupDef } from '@ag-grid-enterprise/all-modules';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { RootState } from 'data/root.reducers';
import { WithAuditDetails, AuditDetails } from 'data/types';
import {
  UtimacoCircuitSwitch,
  UtimacoCircuitSwitchPrivileges,
} from 'data/utimacoCircuitSwitch/utimacoCircuitSwitch.types';
import * as actions from 'data/utimacoCircuitSwitch/utimacoCircuitSwitch.actions';
import * as selectors from 'data/utimacoCircuitSwitch/utimacoCircuitSwitch.selectors';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as accessFunctionActions from 'data/accessFunction/accessFunction.actions';
import * as collectionFunctionGroupActions from 'data/collectionFunctionGroup/collectionFunctionGroup.actions';
import * as collectionFunctionActions from 'data/collectionFunction/collectionFunction.actions';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import * as accessFunctionSelectors from 'data/accessFunction/accessFunction.selectors';
import * as collectionFunctionSelectors from 'data/collectionFunction/collectionFunction.selectors';
import * as collectionFunctionGroupSelectors from 'data/collectionFunctionGroup/collectionFunctionGroup.selectors';
import {
  AuditActionType,
  AuditService,
  getAuditDetails,
  getAuditFieldInfo,
} from 'shared/userAudit/audit-constants';
import * as supportedFeaturesSelectors from 'data/supportedFeatures/supportedFeatures.selectors';
import { AppToaster } from 'shared/toaster';
import cloneDeep from 'lodash.clonedeep';
import UtimacoCircuitSwitchListGrid from './components/UtimacoCircuitSwitchListGrid';
import { circuitSwitchColumnDefs } from '../../UtimacoCircuitSwitch.types';

type RouterParams = {
  history: string
}

const mapState = (state: RootState) => ({
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  userId: authenticationSelectors.getAuthenticationUserId(state),
  userName: authenticationSelectors.getAuthenticationUserName(state),
  beforeLogout: state.authentication.beforeLogout,
  privileges: state.authentication.privileges,
  deliveryFunctionId: state.global.selectedDF,
  data: selectors.getUtimacoCircuitSwitchList(state),
  selectedUtimacoCircuitSwitch: selectors.getSelectedUtimacoCircuitSwitch(state),
  userDBId: state.authentication.userDBId,
  enabledAccessFunctions: supportedFeaturesSelectors.getEnabledAccessFunction(state),
  accessFunctions: accessFunctionSelectors.getAccessFunctions(state),
  collectionFunctions: collectionFunctionSelectors.getCollectionFunctionList(state),
  collectionFunctionGroups: collectionFunctionGroupSelectors.getCollectionFunctionGroupList(state),
});

const mapDispatch = {
  goToUtimacoCircuitSwitchCreate: actions.goToUtimacoCircuitSwitchCreate,
  goToUtimacoCircuitSwitchEdit: actions.goToUtimacoCircuitSwitchEdit,
  goToUtimacoCircuitSwitchView: actions.goToUtimacoCircuitSwitchView,
  selectUtimacoCircuitSwitch: actions.selectUtimacoCircuitSwitch,
  getUtimacoCircuitSwitchList: actions.getUtimacoCircuitSwitchList,
  deleteUtimacoCircuitSwitch: actions.deleteUtimacoCircuitSwitch,
  getAccessFunctions: accessFunctionActions.getAccessFunctionsWithViewOptions,
  getCollectionFunctionGroups: collectionFunctionGroupActions.getCollectionFunctionGroupList,
  fetchCollectionFunctionList: collectionFunctionActions.fetchCollectionFunctionList,
};

const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>
type PropsFromRouter = RouteComponentProps<RouterParams>

export type Props = PropsFromRedux & PropsFromRouter

export const UtimacoCircuitSwitchList: FunctionComponent<Props> = ({
  isAuditEnabled,
  userId,
  userName,
  history,
  beforeLogout,
  privileges,
  deliveryFunctionId,
  data,
  selectedUtimacoCircuitSwitch,
  accessFunctions,
  collectionFunctions,
  collectionFunctionGroups,
  goToUtimacoCircuitSwitchCreate,
  goToUtimacoCircuitSwitchEdit,
  goToUtimacoCircuitSwitchView,
  selectUtimacoCircuitSwitch,
  getUtimacoCircuitSwitchList,
  deleteUtimacoCircuitSwitch,
  getAccessFunctions,
  getCollectionFunctionGroups,
  fetchCollectionFunctionList,
}: Props) => {
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date>();
  const [dataGrid, setDataGrid] = useState<UtimacoCircuitSwitch[] | []>([]);
  const [columnDefs] = useState<ColGroupDef[]>(circuitSwitchColumnDefs);

  useEffect(() => {
    getUtimacoCircuitSwitchList(deliveryFunctionId);
    getAccessFunctions(deliveryFunctionId);
    getCollectionFunctionGroups(deliveryFunctionId);
    fetchCollectionFunctionList(deliveryFunctionId);
    return () => {
      AppToaster.clear();
    };
  }, [
    deliveryFunctionId,
    getUtimacoCircuitSwitchList,
    getAccessFunctions,
    getCollectionFunctionGroups,
    fetchCollectionFunctionList,
  ]);

  useEffect(() => {
    if (data) {
      const clonedData = cloneDeep(data);

      const formattedData = clonedData.map((packetSwitch) => {
        const accessFunctionName = accessFunctions.find((af) => atob(af.id as string) === packetSwitch.AFID)?.name;
        const cfName = collectionFunctions.find((cf) => atob(cf.id as string) === packetSwitch.CFID)?.name;

        return {
          ...packetSwitch,
          AFID: accessFunctionName as string,
          CFID: cfName as string
        };
      });

      setDataGrid(formattedData as UtimacoCircuitSwitch[]);
    }
  }, [accessFunctions, collectionFunctions, collectionFunctionGroups, data]);

  useEffect(() => {
    setLastUpdatedAt(new Date());
  }, [data]);

  const handleRefresh = () => getUtimacoCircuitSwitchList(deliveryFunctionId);

  const handleCreateNew = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    goToUtimacoCircuitSwitchCreate(history);
  };

  const handleSelectionChanged = (event: SelectionChangedEvent) => {
    selectUtimacoCircuitSwitch(event.api.getSelectedRows());
  };

  const handleShowActionMenuItem = () => true;

  const handleEdit = () => goToUtimacoCircuitSwitchEdit(history);

  const handleSelectItemMenuAction = () => goToUtimacoCircuitSwitchView(history);

  const handleDelete = () => {
    const circuitSwitchID = selectedUtimacoCircuitSwitch[0]?.id;
    if (circuitSwitchID == null) {
      return;
    }

    const body: WithAuditDetails<void> = { data: undefined };

    if (isAuditEnabled) {
      const auditDetails = getAuditDetails(
        isAuditEnabled,
        userId,
        userName,
        getAuditFieldInfo(null),
        selectedUtimacoCircuitSwitch[0]?.ISDN,
        AuditService.UTIMACO_CIRCUIT_SWITCH,
        AuditActionType.UTIMACO_CIRCUIT_SWITCH_DELETE,
      );
      body.auditDetails = auditDetails as AuditDetails;
    }

    deleteUtimacoCircuitSwitch(deliveryFunctionId, circuitSwitchID, body);
  };

  const hasPrivilegeToView = privileges.includes(UtimacoCircuitSwitchPrivileges.View);
  const hasPrivilegesToCreate = privileges.includes(UtimacoCircuitSwitchPrivileges.Edit);
  const hasPrivilegeToDelete = privileges.includes(UtimacoCircuitSwitchPrivileges.Delete);

  return (
    <Fragment>
      <UtimacoCircuitSwitchListGrid
        data={dataGrid}
        hasPrivilegeToView={hasPrivilegeToView}
        hasPrivilegesToCreate={hasPrivilegesToCreate}
        hasPrivilegeToDelete={hasPrivilegeToDelete}
        noResults={false}
        beforeLogout={beforeLogout}
        columnDefs={columnDefs}
        onRefresh={handleRefresh}
        onCreateNew={handleCreateNew}
        onSelectionChanged={handleSelectionChanged}
        onSelectItemMenuAction={handleSelectItemMenuAction}
        onShowActionMenuItem={handleShowActionMenuItem}
        lastUpdatedAt={lastUpdatedAt}
        selectedUtimacoCircuitSwitch={selectedUtimacoCircuitSwitch[0] || []}
        onDeleteRow={handleDelete}
        onEdit={handleEdit}
      />
    </Fragment>
  );
};

export default withRouter(connector(UtimacoCircuitSwitchList));
